// @flow
import React, { useState, useRef } from 'react'
import classnames from 'classnames'
import copy from 'copy-to-clipboard'
import { FormattedMessage } from 'react-intl'
import { EMBED_URL } from '../../../config'

import styles from './embed.module.scss'
import Button from '@components/common/button/button'

interface Props {
  url: string
  type: 'sound' | 'album'
  id: string
  onCancel: () => void
}

const Embed = ({ type, id, url, onCancel }: Props) => {
  const urlInputRef = useRef<HTMLInputElement | null>(null)
  const embedInputRef = useRef<HTMLInputElement | null>(null)
  const iframeRef = useRef(null)
  const [ isLoaded, setLoaded ] = useState(false)
  const [ showLinkMsg, setShowLinkMsg ] = useState(false)
  const [ showEmbedMsg, setShowEmbedMsg ] = useState(false)

  const onUrlFocus = () => {
    urlInputRef.current.setSelectionRange(0, urlInputRef.current.value.length)
  }

  const onEmbedFocus = () => {
    embedInputRef.current.setSelectionRange(
      0,
      embedInputRef.current.value.length,
    )
  }

  const onLinkCopy = () => {
    copy(urlInputRef.current.value)
    setShowLinkMsg(true)
    setTimeout(() => setShowLinkMsg(false), 3000)
  }

  const onEmbedCopy = () => {
    copy(embedInputRef.current.value)
    setShowEmbedMsg(true)
    setTimeout(() => setShowEmbedMsg(false), 3000)
  }

  const frameHeight = type === 'sound' ? 242 : 342

  return (
    <div className={`${styles.container} container-sm`}>
      <h2 className="tx-oswald tx-w700 mb-5">Share to Your World!</h2>
      <div className={styles.linkContainer}>
        <h3 className="tx-20">
          <FormattedMessage id="embed.link.title" defaultMessage="Share link" />
        </h3>
        <p className="tx-gish mb-3">
          <FormattedMessage
            id="embed.link.lead"
            // eslint-disable-next-line max-len
            defaultMessage="Copy the link and send it to your friends!"
          />
        </p>
        <div className="d-flex">
          <input
            ref={urlInputRef}
            className={`${styles.input} flex1 mr-1`}
            type="text"
            value={url}
            onClick={onUrlFocus}
            readOnly
          />
          <button
            type="button"
            aria-label="Copy to clipboard"
            className={styles.copyBtn}
            onClick={onLinkCopy}
          >
            <span className="solicon-clipboard" />
            <span
              className={classnames(styles.text, { [styles.on]: showLinkMsg })}
            >
              Copied
            </span>
          </button>
        </div>
      </div>
      <div className={styles.embedContainer}>
        <h3 className="tx-20">
          <FormattedMessage
            id="embed.embed.title"
            defaultMessage="Embed code"
          />
        </h3>
        <p className="tx-gish mb-3">
          <FormattedMessage
            id="embed..embed.lead"
            // eslint-disable-next-line max-len
            defaultMessage="Copy this code and embed the widget to your website or blog."
          />
        </p>
        <div className="d-flex mb-5">
          <input
            ref={embedInputRef}
            className={`${styles.input} flex1 mr-1`}
            type="text"
            // eslint-disable-next-line max-len
            value={`<iframe src="${EMBED_URL}/${type}/${id}" width="100%" height="${frameHeight}" frameborder="0"></iframe>`}
            onClick={onEmbedFocus}
            readOnly
          />
          <button
            type="button"
            aria-label="Copy to clipboard"
            className={styles.copyBtn}
            onClick={onEmbedCopy}
          >
            <span className="solicon-clipboard" />
            <span
              className={classnames(styles.text, { [styles.on]: showEmbedMsg })}
            >
              Copied
            </span>
          </button>
        </div>
        <h4 className="tx-w500 tx-g600 mb-3">
          <FormattedMessage
            id="embed.embed.preview"
            defaultMessage="Embed preview"
          />
        </h4>
        <div
          className={classnames(styles.frameContainer, {
            [styles.loaded]: isLoaded,
          })}
        >
          <iframe
            ref={iframeRef}
            title="embed title"
            // eslint-disable-next-line max-len
            src={`${EMBED_URL}/${type}/${id}`}
            width="100%"
            height={frameHeight}
            frameBorder="0"
            onLoad={() => setLoaded(true)}
          />
        </div>
      </div>
      <div className="tx-center py-5">
        <Button onClick={onCancel}>
          <FormattedMessage id="app.close" defaultMessage="Close" />
        </Button>
      </div>
    </div>
  )
}

export default Embed
